import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useLocation } from 'react-router-dom';

import { endpoints } from '@/api/endpoints';
import { LoadingState, ErrorState } from '@/components/states';
import { PanelBoardEditor } from '@/components/panel-board/PanelBoardEditor';
import type { BoardLayout, CalcGenerateResponse } from '@/types/api';

interface ManualPanelBoardState {
  layout?: BoardLayout;
  calcResult?: CalcGenerateResponse;
  generationMode?: string;
}

function isBoardLayout(value: unknown): value is BoardLayout {
  if (!value || typeof value !== 'object') return false;
  const layout = value as Partial<BoardLayout>;
  return (
    typeof layout.layout_id === 'string' &&
    typeof layout.rows === 'number' &&
    typeof layout.modules_per_row === 'number' &&
    Array.isArray(layout.modules) &&
    Array.isArray(layout.devices) &&
    Array.isArray(layout.groups) &&
    Array.isArray(layout.circuits)
  );
}

export function PanelBoardPage() {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const state = location.state as ManualPanelBoardState | null;
  const manualLayout = isBoardLayout(state?.layout) ? state.layout : null;
  const manualBackState = state?.calcResult
    ? { calcResult: state.calcResult, generationMode: state.generationMode }
    : undefined;

  const query = useQuery({
    queryKey: ['design', id],
    queryFn: () => endpoints.getDesign(id!),
    enabled: !!id,
  });

  if (!id && !manualLayout) {
    return (
      <div className="animate-fade-up">
        <Link
          to="/manual"
          state={manualBackState}
          className="text-xs font-medium text-ink-3 hover:text-accent transition-colors mb-4 inline-block"
        >
          {t('panel_board.back_to_manual_project')}
        </Link>
        <ErrorState error={new Error('Panel layout is not available. Generate a manual calculation first.')} />
      </div>
    );
  }

  const layout = id ? query.data?.layout : manualLayout;

  return (
    <div className="animate-fade-up">
      <Link
        to={id ? `/designs/${id}` : '/manual'}
        state={id ? undefined : manualBackState}
        className="text-xs font-medium text-ink-3 hover:text-accent transition-colors mb-4 inline-block"
      >
        {id ? t('panel_board.back_to_design') : t('panel_board.back_to_manual_project')}
      </Link>

      <h1 className="font-sans text-xl font-semibold tracking-[-0.015em] mb-6">
        {id && query.data?.project_label
          ? `${query.data.project_label} — ${t('panel_board.title')}`
          : t('panel_board.title')}
      </h1>

      {id && query.isLoading && <LoadingState />}
      {id && query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {layout && <PanelBoardEditor layout={layout} />}
    </div>
  );
}
